import { ICommandBarItemProps, IImageProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import {
  IPCommandBar,
  IPDefaultButton,
  IPImage,
  IPImageFit,
  IPLabel,
  IPLink,
  IPSeparator,
  IPSpinner,
  IPText,
} from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import {
  createNewApprovalTaskForCurrentUser,
  createNewApprovalTaskForOthers,
} from 'components/Approval/ApprovalHelpers';
import { INewApproval } from 'components/Approval/CreateApproval';
import { EditNewApprovalModal } from 'components/Approval/EditNewApprovalModal';
import { LatestApproval } from 'components/Approval/LatestApproval';
import { DialogOk } from 'components/Dialogs/DialogOk';
import GenericModal from 'components/Dialogs/GenericModal';
import HrefLink from 'components/Links/HrefLink';
import OverlayLoader from 'components/Loading/OverlayLoader';
import { LearnMore } from 'components/Notification/Info';
import { ISingleTag, SingleTag } from 'components/Tags/SingleTag';
import SingleTask from 'components/Tasks/SingleTask';
import { JSONViewer } from 'components/Utils/JSONViewer';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import { globalAppName, globalKB_howToWorkWithKPIEvidence } from 'globalConstants';
import {
  globalStackItemStylesPaddingScene,
  globalStackTokensGapLarge,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
} from 'globalStyles';
import { DriveItem } from 'microsoft-graph';
import { AuthSchemaLineOperation } from 'models/auth/authSchemaLine';
import { PermissionTypes } from 'models/auth/rolePermission';
import KPI from 'models/kpi/kpi';
import KPIDataTransform, { KPIDataTransformStatus } from 'models/kpi/kpiDataTransform';
import ResourceLink from 'models/resourceLink';
import { isBrokeredProvider } from 'models/documentProvider';
import { DocumentOutputPurpose } from 'models/documentOutputLocation';
import ResourceList from 'models/resourceList';
import {
  apiGetLinkContentFile,
  apiUploadListItem,
  UploadConflictBehavior,
} from 'services/Api/linkContentService';
import { SharepointLibraries } from 'models/setting';
import Task from 'models/tasks/task';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { apiGetKPIDataTransform, apiUpdateKPIDataTransform } from 'services/Api/kpiService';
import { apiCreateLink, apiGetLinkForName } from 'services/Api/linkService';
import { apiAddTask, apiGetTaskInstances } from 'services/Api/taskService';
import { apiRequest, GraphScopeId } from 'services/Auth/authConfig';
import {
  hasUserDataPermission,
  hasUserFeatureGenericManager,
  hasUserRolePermission,
} from 'services/Auth/featurePermissions';
import Config from 'services/Config/configService';
import { getGraphSharepointErrorMsg, isUploadCancelledError } from 'services/Graph/graphErrors';
import {
  getValidFilename,
  graphCheckWriteAccessInDocumentLibrary,
  graphGetDriveItemContent,
  graphUploadFileToFolder,
} from 'services/Graph/graphServiceDrive';
import AppError from 'utils/appError';
import { newGuid } from 'utils/guid';
import KPIEvidenceTransformModal from './KPIEvidenceTransformModal';
import NotAcceptedEvidenceConfigModal from './NotAcceptedEvidenceConfigModal';
import NotAcceptedEvidenceHistoryModal from './NotAcceptedEvidenceHistoryModal';

interface ITabEvidenceProps {
  selectedKPI?: KPI;
  isLoading: boolean;
  isBusy: () => boolean;
}

const TabEvidence = (props: ITabEvidenceProps) => {
  const appContext = React.useContext(AppContext);
  const { t } = useTranslation(['translation', 'kpi', 'kpis', 'task', 'library']);
  const [isLoading, setIsLoading] = React.useState<boolean>(props.isLoading);
  const [isUploading, setIsUploading] = React.useState<boolean>(false);
  const [kpiId, setKPIId] = React.useState<number | undefined>(undefined);
  const [transform, setTransform] = React.useState<KPIDataTransform | undefined>(undefined);
  const [showTransformModal, setShowTransformModal] = React.useState<boolean>(false);
  const [showHistoryModal, setShowHistoryModal] = React.useState<boolean>(false);
  const [showConfigModal, setShowConfigModal] = React.useState<boolean>(false);
  const [showEvidenceDialog, setShowEvidenceDialog] = React.useState<boolean>(false);
  const [status, setStatus] = React.useState<KPIDataTransformStatus>(KPIDataTransformStatus.None);
  const [spWriteError, setSpWriteError] = React.useState<string | undefined>(undefined);
  const [spError, setSpError] = React.useState<string | undefined>(undefined);
  const [evidenceList, setEvidenceList] = React.useState<ResourceList | undefined>(undefined);
  const [approvalTaskForUser, setApprovalTaskForUser] = React.useState<Task | undefined>(undefined);
  const [showSelectApproversModal, setShowSelectApproversModal] = React.useState<boolean>(false);
  const [newApprovalForOthers, setNewApprovalForOthers] = React.useState<INewApproval | undefined>(undefined);
  const isReadOnly = !hasUserDataPermission(
    appContext,
    [props.selectedKPI?.authSchemaId],
    AuthSchemaLineOperation.Update,
  );

  const [spLibraryEnabled] = React.useState<boolean>(
    appContext.globalDataCache.settings.get(SharepointLibraries) as boolean,
  );

  const history = useHistory();
  const statusTagColor = '#b0deff';
  const statusTagColorDark = '#647f91';

  //Power Automate logo
  const imagePropsPALogo: IImageProps = {
    src: `${Config.getImageURL()}/ms-power-automate.png`,
    imageFit: IPImageFit.contain,
    width: 80,
    height: 80,
  };

  //
  // Effects
  //

  React.useEffect(() => {
    //react to another KPI
    if (props.selectedKPI?.kpiId !== kpiId) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedKPI]);

  //
  // Load data
  //
  const loadData = async () => {
    try {
      if (!props.selectedKPI?.kpiId || !spLibraryEnabled) return;

      setIsLoading(true);
      await appContext.globalDataCache.lists.getItems();
      await appContext.globalDataCache.templates.getItems();

      setKPIId(props.selectedKPI.kpiId);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const transform = await apiGetKPIDataTransform(props.selectedKPI.kpiId, accessToken, appContext.globalDataCache);

      const evidenceList = getEvidenceList();

      if (transform && transform.link && evidenceList) {
        const evidence = await downloadEvidence(transform, evidenceList);
        if (evidence === '') {
          //there was an error downloading the evidence
          //- could be deleted from SharePoint
          transform.evidence = undefined;
          transform.link = undefined;
          transform.linkId = undefined;
        } else {
          transform.evidence = evidence;
        }
      }

      setTransform(transform);

      if (transform) {
        setStatus(transform.getStatus());
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getEvidenceList = (): ResourceList | undefined => {
    //the category of the reference evidence location
    const evidenceList = appContext.globalDataCache.outputLocations.getList(DocumentOutputPurpose.ReferenceEvidence);
    if (!evidenceList) {
      return undefined;
    }

    // altTenantId is a SharePoint cross-tenant concern; a provider category never has one
    if (!evidenceList.spDriveId || !evidenceList.spDriveItemId) {
      // a provider category saved without a folder would otherwise leave the upload silently dead
      if (isBrokeredProvider(evidenceList)) {
        appContext.showNotification(t('kpi:TabEvidence.Evidence.CategoryNotBound', { name: evidenceList.name }), true);
      }

      return undefined;
    }

    if (!isBrokeredProvider(evidenceList) && evidenceList.altTenantId) {
      return undefined;
    }

    if (hasUserRolePermission(appContext, PermissionTypes.CreateLibraryItem) === false) {
      appContext.showNotification(t('kpi:TabEvidence.Evidence.NoPermissionToCreateEvidence'), true);

      return undefined;
    }

    setEvidenceList(evidenceList);

    return evidenceList;
  };

  const downloadEvidence = async (
    transform: KPIDataTransform,
    evidenceList: ResourceList,
  ): Promise<string | undefined> => {
    if (!transform || !evidenceList.spDriveId || !transform.link?.driveItemId) return;

    if (isBrokeredProvider(evidenceList)) {
      try {
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const blob = await apiGetLinkContentFile(transform.link.linkId, accessToken);

        return await blob.text();
      } catch {
        return '';
      }
    }

    try {
      const graphInterface = await appContext.getGraphInterface(GraphScopeId.Library, undefined);
      const blob = await graphGetDriveItemContent(
        graphInterface.client,
        evidenceList.spDriveId,
        transform.link.driveItemId,
      );

      if (blob) {
        await transform.link.setMetaData(undefined, graphInterface, appContext);

        return await blob.text();
      } else {
        return undefined;
      }
    } catch {
      return '';
    }
  };

  const getEvidenceLink = (): JSX.Element | null => {
    if (status === KPIDataTransformStatus.None || status === KPIDataTransformStatus.New) {
      return null;
    }

    return (
      <IPStack>
        <IPLink onClick={() => setShowEvidenceDialog(true)}>{t('kpi:TabEvidence.Dialogs.Evidence.Link')}</IPLink>
        <GenericModal
          isOpen={showEvidenceDialog}
          title={t('kpi:TabEvidence.Dialogs.Evidence.Title')}
          onClose={() => setShowEvidenceDialog(false)}
          minWidth={250}
          minHeight={200}
          width={'50vw'}
          height={'50vh'}
          maxHeight={'95vh'}
          maxWidth={'95vw'}
        >
          <IPSeparator />
          <IPStack verticalFill>
            <ScrollableStackItem>
              <JSONViewer data={transform?.evidence} />
            </ScrollableStackItem>
          </IPStack>
        </GenericModal>
      </IPStack>
    );
  };

  const getStatusTag = (): ISingleTag => {
    const tag: ISingleTag = {
      tagId: newGuid(),
      tagName: getStatusText(),
      tagColor: appContext.useDarkMode ? statusTagColorDark : statusTagColor,
    };

    return tag;
  };

  const uploadEvidence = async (): Promise<ResourceLink | undefined> => {
    try {
      if (
        !props.selectedKPI ||
        !evidenceList?.spDriveId ||
        !evidenceList.spDriveItemId ||
        !transform ||
        !transform.evidence
      ) {
        return;
      }

      setIsUploading(true);

      if (isBrokeredProvider(evidenceList)) {
        return await uploadEvidenceToProvider(evidenceList);
      }

      //check write access
      const graphInterface = await appContext.getGraphInterface(GraphScopeId.Library, undefined);

      try {
        await graphCheckWriteAccessInDocumentLibrary(graphInterface.client, evidenceList.spDriveId);
      } catch (err) {
        const appErr = AppError.fromGraphError(err);
        const spErr = getGraphSharepointErrorMsg(appErr, t);
        if (spErr) {
          setSpWriteError(spErr);

          return;
        }
      }

      //create a new link or get the existing link
      let newLink: ResourceLink | undefined = transform.link;

      if (!newLink) {
        newLink = new ResourceLink();
        newLink.linkName = t('kpi:TabEvidence.Evidence.UploadDefaultFilename', {
          id: props.selectedKPI.kpiId,
          name: props.selectedKPI.name,
        });
        newLink.linkName = getValidFilename(newLink.linkName);
        newLink.list = evidenceList;
        newLink.listId = evidenceList.listId;
      }

      //upload the evidence
      let uploadedItem: DriveItem | undefined = undefined;

      try {
        const blob = new Blob([transform.evidence], { type: 'plain/text' });
        const file = new File([blob], newLink.linkName);

        uploadedItem = await graphUploadFileToFolder(
          graphInterface.client,
          file,
          evidenceList.spDriveId,
          evidenceList.spDriveItemId, //upload to currently selected folder or root folder of list if none selected
          newLink.linkName,
          t('library:Picker.UploadDescription', { appName: globalAppName }),
          undefined,
          'replace',
        );
      } catch (err) {
        //a cancel is a user action and needs no message
        if (isUploadCancelledError(err)) {
          return;
        }

        const appErr = AppError.fromGraphError(err);
        const spErr = getGraphSharepointErrorMsg(appErr, t);
        if (spErr) {
          setSpError(spErr);

          return;
        }

        //an error without a Sharepoint message must still reach the caller instead of being
        //reported as a generic upload failure
        throw appErr;
      }

      //Save result to back-end
      if (!uploadedItem) {
        throw new AppError('Could not upload evidence: unknown error');
      }

      newLink.driveItemId = uploadedItem?.id;
      newLink.linkURL = uploadedItem.webUrl ?? '';

      //if the link with the same name exists, use that link, otherwise create a new link
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      let createdLink = await apiGetLinkForName(
        newLink.listId,
        newLink.linkName,
        accessToken,
        appContext.globalDataCache,
      );
      if (!createdLink || !createdLink.linkId) {
        createdLink = await apiCreateLink(newLink, accessToken, appContext.globalDataCache);
      }

      const newtransform = transform.clone();
      newtransform.linkId = createdLink.linkId;
      newtransform.link = createdLink;
      await updateTransform(newtransform);

      return createdLink;
    } catch (err) {
      appContext.setError(err);

      return undefined;
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * The provider twin of the SharePoint upload above. The broker writes the file with the
   * tenant's stored credentials and creates the library item in the same call, so there is no
   * separate write-access probe: a refusal comes back as the upload error itself.
   */
  const uploadEvidenceToProvider = async (evidenceList: ResourceList): Promise<ResourceLink | undefined> => {
    if (!props.selectedKPI || !transform?.evidence) return undefined;

    const fileName = getValidFilename(
      transform.link?.linkName ??
        t('kpi:TabEvidence.Evidence.UploadDefaultFilename', {
          id: props.selectedKPI.kpiId,
          name: props.selectedKPI.name,
        }),
    );

    const blob = new Blob([transform.evidence], { type: 'plain/text' });
    const file = new File([blob], fileName);

    const accessToken = await appContext.getAccessToken(apiRequest.scopes);
    const result = await apiUploadListItem(
      evidenceList.listId,
      file,
      evidenceList.spDriveItemId,
      UploadConflictBehavior.Replace,
      true,
      fileName,
      accessToken,
      appContext.globalDataCache,
    );

    if (!result.link) {
      throw new AppError('Could not upload evidence: the provider returned no library item');
    }

    const newTransform = transform.clone();
    newTransform.linkId = result.link.linkId;
    newTransform.link = result.link;
    await updateTransform(newTransform);

    return result.link;
  };

  const updateTransform = async (transform: KPIDataTransform) => {
    const accessToken = await appContext.getAccessToken(apiRequest.scopes);
    await apiUpdateKPIDataTransform(transform, accessToken, appContext.globalDataCache);
    setTransform(transform);
    setStatus(transform.getStatus());
  };

  const onApproveByMe = async () => {
    try {
      if (!transform) return;
      const link = await uploadEvidence();
      if (link) {
        //create an approval for this evidence link for the current user
        setIsUploading(true);

        const approvalTask = createNewApprovalTaskForCurrentUser(link, appContext, t);
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const createdTask = await apiAddTask(approvalTask, false, accessToken, appContext.globalDataCache);

        if (createdTask) {
          const newTransform = transform.clone();
          newTransform.linkId = link.linkId;
          newTransform.link = link;
          await updateTransform(newTransform);
          const instances = await apiGetTaskInstances(createdTask.taskId, accessToken, appContext.globalDataCache);
          if (instances.length > 0) {
            setApprovalTaskForUser(instances[0]);
          }
        }
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsUploading(false);
    }
  };

  const onApproveByOther = async () => {
    try {
      if (!transform) return;
      const link = await uploadEvidence();
      if (link) {
        //update the transform info with the created link
        const newTransform = transform.clone();
        newTransform.linkId = link.linkId;
        newTransform.link = link;
        await updateTransform(newTransform);

        //let the user select the approvers
        const approvalTask = createNewApprovalTaskForOthers(link, appContext, t);

        const newApproval: INewApproval = {
          task: approvalTask,
          item: link,
          isBusy: false,
        };

        setNewApprovalForOthers(newApproval);
        setShowSelectApproversModal(true);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsUploading(false);
    }
  };

  const onSaveApprovalForOthers = async (newApprovals: INewApproval[]) => {
    try {
      if (newApprovals.length === 0 || !newApprovalForOthers) return;

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      await apiAddTask(newApprovalForOthers.task, false, accessToken, appContext.globalDataCache);

      const msg = t('kpi:TabEvidence.Evidence.Status.Selected.ApproveByOtherSent');
      appContext.showNotification(msg);
      await loadData();
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsUploading(false);
    }
  };

  const onSave = async (newTransform: KPIDataTransform) => {
    try {
      setShowTransformModal(false);
      setShowHistoryModal(false);
      setShowConfigModal(false);
      await updateTransform(newTransform);
    } catch (err) {
      appContext.setError(err);

      return undefined;
    }
  };

  const navigateToOutputLocations = () => {
    history.push('/admin/settings/reports');
  };

  const onRefresh = () => {
    loadData();
  };

  const saveTask = async (task: Task, isNew: boolean, isCancelled: boolean) => {
    try {
      setApprovalTaskForUser(undefined);
      await loadData();
    } catch (err) {
      appContext.setError(err);
    }
  };

  const removeTask = async (task: Task) => {
    setApprovalTaskForUser(undefined);
    //not possible
  };

  const updateTask = async (task: Task) => {
    try {
      setApprovalTaskForUser(undefined);
      await loadData();
    } catch (err) {
      appContext.setError(err);
    }
  };

  //
  // Helpers
  //
  const getCommandBarButtons = (): ICommandBarItemProps[] => {
    const items: ICommandBarItemProps[] = [
      {
        key: 'notApprovedEvidence',
        disabled: props.isBusy() || isLoading || status === KPIDataTransformStatus.None,
        text: t('kpi:TabEvidence.Commands.NotApprovedEvidence'),
        iconProps: { iconName: 'History' },
        onClick: () => setShowHistoryModal(true),
      },
      {
        key: 'rejectedEvidenceAction',
        disabled: props.isBusy() || isLoading || status === KPIDataTransformStatus.None,
        text: t('kpi:TabEvidence.Commands.RejectedEvidenceAction'),
        iconProps: { iconName: 'SetAction' },
        onClick: () => setShowConfigModal(true),
      },
    ];

    return items;
  };

  const getStatusView = (status: KPIDataTransformStatus): JSX.Element | null => {
    switch (status) {
      case KPIDataTransformStatus.None:
        return (
          <IPStack verticalFill tokens={globalStackTokensGapSmall}>
            <IPStack.Item>
              <IPLabel>{t('kpi:TabEvidence.Evidence.Action.Label')}</IPLabel>
              <IPText>{getStatusInfo()}</IPText>
              <LearnMore learnMoreLink={globalKB_howToWorkWithKPIEvidence} />
            </IPStack.Item>
          </IPStack>
        );
      case KPIDataTransformStatus.New:
        return (
          <IPStack verticalFill tokens={globalStackTokensGapSmall}>
            <IPStack.Item>
              <IPLabel>{t('kpi:TabEvidence.Evidence.Action.Label')}</IPLabel>
              <IPText>{getStatusInfo()}</IPText>
            </IPStack.Item>
            <IPStack.Item>
              <IPDefaultButton
                text={t('kpi:TabEvidence.Evidence.Status.New.SelectButton')}
                onClick={() => setShowTransformModal(true)}
              />
            </IPStack.Item>
          </IPStack>
        );
      case KPIDataTransformStatus.Selected:
        if (!appContext.globalDataCache.outputLocations.getList(DocumentOutputPurpose.ReferenceEvidence)) {
          return (
            <IPStack verticalFill tokens={globalStackTokensGapSmall}>
              <IPStack.Item>
                <IPLabel>{t('kpi:TabEvidence.Evidence.Action.Label')}</IPLabel>
                <IPText>{t('kpi:TabEvidence.Evidence.UploadConfiguration')}</IPText>
              </IPStack.Item>
              {hasUserFeatureGenericManager(appContext) && (
                <IPStack.Item>
                  <IPDefaultButton
                    text={t('kpi:TabEvidence.Evidence.UploadConfigurationLink')}
                    onClick={() => navigateToOutputLocations()}
                  />
                </IPStack.Item>
              )}
              <IPStack.Item>
                <IPLink
                  onClick={() => {
                    const newTransform = transform?.clone();
                    if (newTransform) {
                      newTransform.evidence = undefined;
                      newTransform.link = undefined;
                      newTransform.linkId = undefined;
                      onSave(newTransform);
                    }
                  }}
                >
                  {t('kpi:TabEvidence.Evidence.Status.Selected.PrevStatus')}
                </IPLink>
              </IPStack.Item>
            </IPStack>
          );
        } else {
          return (
            <IPStack verticalFill tokens={globalStackTokensGapSmall}>
              <IPStack.Item>
                <IPLabel>{t('kpi:TabEvidence.Evidence.Action.Label')}</IPLabel>
                <IPText>{getStatusInfo()}</IPText>
              </IPStack.Item>
              <IPStack.Item>
                <IPStack horizontal tokens={globalStackTokensGapSmall}>
                  <IPDefaultButton
                    text={t('kpi:TabEvidence.Evidence.Status.Selected.ApproveByMe')}
                    onClick={() => onApproveByMe()}
                  />
                  <IPDefaultButton
                    text={t('kpi:TabEvidence.Evidence.Status.Selected.ApproveByOther')}
                    onClick={() => onApproveByOther()}
                  />
                </IPStack>
              </IPStack.Item>
              <IPStack.Item>
                <IPLink
                  onClick={() => {
                    const newTransform = transform?.clone();
                    if (newTransform) {
                      newTransform.evidence = undefined;
                      newTransform.link = undefined;
                      newTransform.linkId = undefined;
                      onSave(newTransform);
                    }
                  }}
                >
                  {t('kpi:TabEvidence.Evidence.Status.Selected.PrevStatus')}
                </IPLink>
              </IPStack.Item>
            </IPStack>
          );
        }
      case KPIDataTransformStatus.ApprovalPending:
        if (!transform || !transform.link || transform.link.approvals?.length === 0) return null;

        return (
          <IPStack tokens={globalStackTokensGapSmall}>
            <IPStack.Item>
              <IPLabel>{t('kpi:TabEvidence.Evidence.ApprovalStatus')}</IPLabel>
              {transform.link?.approvals && !transform.link.approvals[0].mainTask.webhookStatus && (
                <IPLink
                  onClick={() => {
                    if (
                      transform.link?.approvals &&
                      transform.link.approvals.length > 0 &&
                      transform.link?.approvals[0].approvalTasks.length > 0
                    ) {
                      setApprovalTaskForUser(transform.link.approvals[0].approvalTasks[0]);
                    }
                  }}
                >
                  {t('kpi:TabEvidence.Evidence.Status.ApprovalPending.OpenTaskLink')}
                </IPLink>
              )}
            </IPStack.Item>
            <IPStack.Item>
              <LatestApproval link={transform.link} approvals={transform.link.approvals} showHistoryLink={true} />
            </IPStack.Item>
            <IPStack.Item>
              <IPLink
                onClick={() => {
                  const newTransform = transform?.clone();
                  if (newTransform) {
                    newTransform.linkId = undefined;
                    newTransform.link = undefined;
                    onSave(newTransform);
                  }
                }}
              >
                {t('kpi:TabEvidence.Evidence.Status.Selected.PrevStatus')}
              </IPLink>
            </IPStack.Item>
          </IPStack>
        );
      case KPIDataTransformStatus.Approved:
        if (!transform || !transform.link) return null;

        return (
          <IPStack tokens={globalStackTokensGapSmall}>
            <IPStack.Item>
              <IPLabel>{t('kpi:TabEvidence.Evidence.ApprovalStatus')}</IPLabel>
              <IPLink
                onClick={() => {
                  if (
                    transform.link?.approvals &&
                    transform.link.approvals.length > 0 &&
                    transform.link?.approvals[0].approvalTasks.length > 0
                  ) {
                    setApprovalTaskForUser(transform.link.approvals[0].approvalTasks[0]);
                  }
                }}
              >
                {t('kpi:TabEvidence.Evidence.Status.Approved.OpenTaskLink')}
              </IPLink>
            </IPStack.Item>
            <IPStack.Item>
              <LatestApproval link={transform.link} approvals={transform.link.approvals} showHistoryLink={true} />
            </IPStack.Item>
          </IPStack>
        );
    }
  };

  const getStatusText = (): string => {
    switch (status) {
      case KPIDataTransformStatus.None:
        return t('kpi:TabEvidence.Evidence.Status.None.Label');
      case KPIDataTransformStatus.New:
        return t('kpi:TabEvidence.Evidence.Status.New.Label');
      case KPIDataTransformStatus.Selected:
        return t('kpi:TabEvidence.Evidence.Status.Selected.Label');
      case KPIDataTransformStatus.ApprovalPending:
        return t('kpi:TabEvidence.Evidence.Status.ApprovalPending.Label');
      case KPIDataTransformStatus.Approved:
        return t('kpi:TabEvidence.Evidence.Status.Approved.Label');
      default:
        return '';
    }
  };

  const getStatusInfo = (): string => {
    switch (status) {
      case KPIDataTransformStatus.None:
        return t('kpi:TabEvidence.Evidence.Status.None.Info');
      case KPIDataTransformStatus.New:
        return t('kpi:TabEvidence.Evidence.Status.New.Info');
      case KPIDataTransformStatus.Selected:
        return t('kpi:TabEvidence.Evidence.Status.Selected.Info');
      case KPIDataTransformStatus.ApprovalPending:
        return t('kpi:TabEvidence.Evidence.Status.ApprovalPending.Info');
      case KPIDataTransformStatus.Approved:
        return t('kpi:TabEvidence.Evidence.Status.Approved.Info');
      default:
        return '';
    }
  };

  //
  // Main render
  //
  if (!spLibraryEnabled) {
    return (
      <IPStack>
        <IPStack.Item styles={globalStackItemStylesPaddingScene}>
          <IPText block>{t('kpi:TabEvidence.Evidence.SharePointLibraryDisabled')}</IPText>
          <HrefLink href="/admin/settings/integration">{t('kpi:TabEvidence.Evidence.SharePointLibraryLink')}</HrefLink>
        </IPStack.Item>
      </IPStack>
    );
  }

  return (
    <Fragment>
      {isUploading && <OverlayLoader text={t('translation:General.Notifications.Loading')} />}
      <IPStack verticalFill tokens={globalStackTokensGapMedium}>
        {!appContext.isMobileView && (
          <IPStack horizontal verticalAlign="start" tokens={globalStackTokensGapLarge}>
            <IPStack.Item>
              <IPImage {...imagePropsPALogo} />
            </IPStack.Item>
            <IPStack.Item styles={{ root: { maxWidth: 400 } }}>
              <IPText>{t('kpi:TabEvidence.SubTitle')}</IPText>
            </IPStack.Item>
          </IPStack>
        )}
        <IPStack.Item>
          {!isReadOnly && (
            <IPCommandBar items={getCommandBarButtons()} styles={{ root: { paddingLeft: 0 } }}></IPCommandBar>
          )}
        </IPStack.Item>
        {isLoading && (
          <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
            <IPSpinner size={IPSpinnerSize.large} />
          </IPStack>
        )}
        {!isLoading && (
          <IPStack.Item>
            <IPLabel>{t('kpi:TabEvidence.Evidence.EvidenceStatus')}</IPLabel>
            <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
              <SingleTag tag={getStatusTag()} minWidth={150} />
              {getEvidenceLink()}
            </IPStack>
          </IPStack.Item>
        )}
        {!isLoading && <IPStack.Item>{getStatusView(status)}</IPStack.Item>}
      </IPStack>
      {transform && (
        <KPIEvidenceTransformModal
          isOpen={showTransformModal}
          transform={transform}
          onClose={() => setShowTransformModal(false)}
          onSave={onSave}
          onRefresh={onRefresh}
        />
      )}
      <DialogOk
        hidden={spWriteError === undefined}
        title={t('kpi:TabEvidence.Dialogs.ErrorWriteAccess.Title')}
        subText={t('kpi:TabEvidence.Dialogs.ErrorWriteAccess.SubTitle', {
          categoryName: evidenceList?.name,
        })}
        optionalJSX={
          <HrefLink href={evidenceList?.webURL} external>
            {t('kpi:TabEvidence.Dialogs.ErrorWriteAccess.OpenSharePointLink')}
          </HrefLink>
        }
        onOk={() => setSpWriteError(undefined)}
      />
      <DialogOk
        hidden={spError === undefined}
        title={t('kpi:TabEvidence.Dialogs.ErrorUpload.Title')}
        subText={t('kpi:TabEvidence.Dialogs.ErrorUpload.SubTitle', {
          categoryName: evidenceList?.name,
        })}
        optionalJSX={
          <HrefLink href={evidenceList?.webURL} external>
            {t('kpi:TabEvidence.Dialogs.ErrorUpload.OpenSharePointLink')}
          </HrefLink>
        }
        onOk={() => setSpError(undefined)}
      />
      {approvalTaskForUser && (
        <SingleTask
          task={approvalTaskForUser}
          isOpen={true}
          close={() => {
            setApprovalTaskForUser(undefined);
          }}
          onSave={saveTask}
          onRemove={removeTask}
          onUpdate={updateTask}
        />
      )}
      {showSelectApproversModal && newApprovalForOthers && (
        <EditNewApprovalModal
          selectedApprovals={[newApprovalForOthers]}
          isOpen={showSelectApproversModal}
          onClose={() => {
            setShowSelectApproversModal(false);
          }}
          onSave={onSaveApprovalForOthers}
        />
      )}
      {showConfigModal && transform && (
        <NotAcceptedEvidenceConfigModal
          transform={transform}
          isOpen={showConfigModal}
          onClose={() => {
            setShowConfigModal(false);
          }}
          onSave={onSave}
        />
      )}
      {showHistoryModal && transform && (
        <NotAcceptedEvidenceHistoryModal
          transform={transform}
          isOpen={showHistoryModal}
          onClose={() => {
            setShowHistoryModal(false);
          }}
          onSubmit={onSave}
        />
      )}
    </Fragment>
  );
};

export default TabEvidence;
